import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Flower2, Camera, MapPin, CalendarCheck, Sparkles, HeartHandshake, ArrowRight, Gift } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import OffertaCura from "@/components/OffertaCura";
import CampaignBanner from "@/components/CampaignBanner";
import ExtraInEvidenza from "@/components/ExtraInEvidenza";
import RicorrenzeTimeline from "@/components/RicorrenzeTimeline";
import InstallPwa from "@/components/InstallPwa";
import WhatsAppLink from "@/components/WhatsAppLink";
import { eur } from "@/components/common";

const HERO_IMG = "/hero-home.jpg?v=3";

const SERVICES = [
  { icon: Sparkles, title: "Pulizia", text: "Tre livelli di cura del luogo: dalla cura ordinaria sempre inclusa alla pulizia specialistica su preventivo.", to: "/servizi/pulizia" },
  { icon: Flower2, title: "Cambio fiori", text: "Fiori freschi a ogni visita, dal mazzo di margherite standard alle composizioni del nostro catalogo.", to: "/servizi/cambio-fiori" },
  { icon: Camera, title: "Fotografia dell'intervento", text: "Al termine di ogni visita ricevi la fotografia: la prova semplice e sincera che siamo stati lì per te.", to: "/servizi/fotografia" },
  { icon: HeartHandshake, title: "Altri servizi dedicati", text: "Esigenze particolari, ricorrenze, richieste fuori dall'ordinario: raccontaci cosa serve.", to: "/servizi/altri" },
  { icon: Gift, title: "Extra", text: "Rosa rossa, composizioni in ciotola, piantine: piccoli pensieri in più da recapitare durante l'intervento.", to: "/extra" },
];

const STEPS = [
  { n: 1, icon: MapPin, title: "Scegli il luogo", text: "Indichi il cimitero, il campo e la posizione approssimativa sulla fotografia aerea." },
  { n: 2, icon: Flower2, title: "Scegli il servizio", text: "Cambio fiori, pulizia approfondita o una richiesta specialistica su preventivo." },
  { n: 3, icon: CalendarCheck, title: "Noi interveniamo", text: "Un professionista locale si reca presso il luogo nel cimitero, con rispetto e attenzione." },
  { n: 4, icon: Camera, title: "Ricevi la fotografia", text: "Al termine ricevi la foto dell'intervento: la prova che siamo stati lì per te." },
];

export default function Home() {
  const [config, setConfig] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [intro] = useState(() => {
    try {
      return !sessionStorage.getItem("sa_intro_seen") &&
        !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch {
      return false;
    }
  });
  const [introGone, setIntroGone] = useState(!intro);
  const buyTrial = () =>
    user ? navigate("/checkout?type=singola") : navigate("/accedi", { state: { from: "/checkout?type=singola" } });

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!intro) return;
    try { sessionStorage.setItem("sa_intro_seen", "1"); } catch { /* noop */ }
    const t = setTimeout(() => setIntroGone(true), 4150);
    return () => clearTimeout(t);
  }, [intro]);

  return (
    <div>
      <section data-testid="hero-section" className="relative bg-[#f8fafc]">
        <div className="relative w-full h-[52vh] sm:h-[68vh] lg:h-[82vh] overflow-hidden">
          <img
            src={HERO_IMG}
            alt="SEMPRE ACCANTO — La cura nel tempo."
            data-testid="hero-image"
            className={`w-full h-full object-cover object-[20%_center] sm:object-center ${intro ? "hero-img-reveal" : ""}`}
            style={{
              WebkitMaskImage: "linear-gradient(to bottom, black 0%, black 74%, transparent 99%)",
              maskImage: "linear-gradient(to bottom, black 0%, black 74%, transparent 99%)",
            }}
          />
          {!introGone && (
            <div data-testid="hero-intro" aria-hidden="true" className="hero-intro absolute inset-0 overflow-hidden">
              <img src="/intro-bg.jpg" alt="" className="hero-intro-bg absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-b from-white/50 via-white/20 to-white/55" />
              <span className="intro-petal" style={{ left: "12%", animationDelay: "0s", animationDuration: "7.5s" }} />
              <span className="intro-petal" style={{ left: "28%", animationDelay: "1.2s", animationDuration: "8.5s", width: 16, height: 22 }} />
              <span className="intro-petal" style={{ left: "48%", animationDelay: "0.6s", animationDuration: "9s", width: 12, height: 17 }} />
              <span className="intro-petal" style={{ left: "66%", animationDelay: "1.8s", animationDuration: "7s", width: 18, height: 24 }} />
              <span className="intro-petal" style={{ left: "82%", animationDelay: "0.9s", animationDuration: "8s" }} />
              <span className="intro-petal" style={{ left: "38%", animationDelay: "2.4s", animationDuration: "9.5s", width: 18, height: 24 }} />
              <span className="intro-petal" style={{ left: "90%", animationDelay: "2.1s", animationDuration: "7.8s", width: 12, height: 17 }} />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative flex items-center justify-center">
                  <div className="hero-intro-glow absolute -inset-14 sm:-inset-24 rounded-full" />
                  <img
                    src="/intro-logo-full.png"
                    alt="SEMPRE ACCANTO — La cura nel tempo."
                    className="hero-intro-logo relative w-64 sm:w-96"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-[28%]"
          style={{ background: "linear-gradient(to bottom, rgba(248,250,252,0) 0%, rgba(248,250,252,0.45) 55%, #f8fafc 96%)" }}
        />
      </section>

      <InstallPwa />

      <CampaignBanner testid="campaign-banner-home" />
      <RicorrenzeTimeline />

      <section data-testid="home-offer" className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-14 sm:pt-8 sm:pb-20">
        <OffertaCura testid="home-offer-card" minimal />
      </section>

      <ExtraInEvidenza />

      <section data-testid="giardinetto-section" className="max-w-6xl mx-auto px-4 sm:px-6 pb-14 sm:pb-20">
        <div className="card-soft p-6 sm:p-8 grid gap-6 md:grid-cols-2 items-center">
          <img src="/giardinetto.jpg" alt="Rifacimento giardinetto del luogo: prima e dopo" data-testid="giardinetto-img" className="w-full rounded-2xl object-cover" />
          <div className="space-y-4">
            <p className="eyebrow">Rifacimento giardinetto</p>
            <h2 className="font-serif text-3xl text-navy">Più cura, sempre accanto.</h2>
            <p className="text-sm text-body leading-relaxed">
              Pulizia e sistemazione, scelta delle piante, sassi e dettagli decorativi:
              ogni luogo è unico, creiamo insieme la soluzione più adatta.
            </p>
            <WhatsAppLink
              message="Buongiorno, vorrei un preventivo per il rifacimento del giardinetto della lapide. Vi invio una foto del luogo e le mie richieste."
              label="Richiedi il tuo preventivo su WhatsApp"
              testid="giardinetto-whatsapp"
              className="btn-primary !inline-flex"
            />
          </div>
        </div>
      </section>

      <section data-testid="trial-section" className="max-w-6xl mx-auto px-4 sm:px-6 pb-14 sm:pb-20">
        <div className="card-ivory p-7 sm:p-8 max-w-2xl mx-auto flex flex-col" data-testid="service-trial-card">
          <p className="eyebrow mb-2">Prima uscita di prova</p>
          <p className="font-serif text-5xl text-navy mb-2">{eur(config?.trial_price ?? 19.9)}</p>
          <p className="text-sm text-body leading-relaxed mb-4">
            Comprende 1 intervento singolo con mazzo di margherite, consegna e intervento
            presso il luogo che ci indicherai.
          </p>
          <p className="text-sm text-body leading-relaxed mb-5">
            Se scegli un abbonamento entro 10 giorni, i {eur(config?.trial_price ?? 19.9)} ti verranno
            riconosciuti come credito sul tuo prossimo abbonamento.
          </p>
          <button onClick={buyTrial} data-testid="trial-buy-btn" className="btn-primary self-start">
            Acquista la prima uscita <ArrowRight size={16} />
          </button>
          <Link to="/abbonamenti" data-testid="trial-abbonamenti-btn" className="btn-secondary mt-3 self-start">
            Abbonamenti
          </Link>
        </div>
      </section>

      <section data-testid="emotional-section" className="bg-[#f8fafc] -mt-[6vh] sm:-mt-[8vh] lg:-mt-[10vh] relative">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20 text-center space-y-6">
          <p className="text-base sm:text-lg text-body leading-relaxed max-w-2xl mx-auto">
            SEMPRE ACCANTO si prende cura dei tuoi cari quando tu non puoi esserci: con rispetto,
            attenzione e continuità, documentando ogni intervento con una fotografia.
          </p>
          <div className="flex justify-center gap-3 flex-wrap pt-2">
            <Link to="/servizi" data-testid="cta-services" className="btn-primary !px-8 !py-3.5">
              Scopri i nostri servizi
            </Link>
            <Link to="/accedi" data-testid="cta-register-grave" className="btn-secondary !px-8 !py-3.5">
              Registrati o accedi
            </Link>
          </div>
        </div>
      </section>

      <section data-testid="services-section" className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <div className="text-center mb-12">
          <p className="eyebrow mb-2">I nostri servizi</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-navy">Cura, in ogni dettaglio.</h2>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Link key={i} to={s.to} data-testid={`service-card-${i}`} className="card-soft p-7 block group">
              <s.icon className="text-[#4a7c59] mb-4" size={28} />
              <p className="font-serif text-2xl text-navy mb-2">{s.title}</p>
              <p className="text-sm text-body leading-relaxed mb-4">{s.text}</p>
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1e3a5f] group-hover:gap-2.5 transition-all duration-200">
                Scopri <ArrowRight size={15} />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section id="come-funziona" data-testid="how-it-works-section" className="bg-[#fefcf9] border-y border-[#ece7dd]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="text-center mb-12">
            <p className="eyebrow mb-2">Come funziona</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-navy">Quattro passi, un solo pensiero in meno.</h2>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s) => (
              <div key={s.n} className="bg-white rounded-2xl border border-[#ece7dd] p-6 relative" data-testid={`how-step-${s.n}`}>
                <span className="absolute top-5 right-6 font-serif text-5xl text-[#e0f2fe] select-none">{s.n}</span>
                <s.icon className="text-[#1e3a5f] mb-4" size={26} />
                <p className="font-semibold text-navy mb-2">{s.title}</p>
                <p className="text-sm text-body leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
          <p className="font-serif text-2xl sm:text-3xl text-navy text-center mt-12 italic">
            “Siamo stati lì per te.”
          </p>
          <div className="text-center mt-8">
            <WhatsAppLink
              label="Hai domande? Scrivici su WhatsApp"
              testid="home-whatsapp-cta"
              className="btn-secondary !inline-flex text-sm font-semibold"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
